(() => {
  "use strict";

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const PASSWORD_RULES = [
    { test: (value) => value.length >= 6, label: "6 caracteres o más" },
    { test: (value) => /[A-ZÁÉÍÓÚÑ]/.test(value), label: "Una mayúscula" },
    { test: (value) => /[a-záéíóúñ]/.test(value), label: "Una minúscula" },
    { test: (value) => /\d/.test(value), label: "Un número" },
    {
      test: (value) => /[^A-Za-z0-9ÁÉÍÓÚÑáéíóúñ]/.test(value),
      label: "Un carácter especial",
    },
  ];

  const trimValue = (value) => String(value || "").trim();

  const parsePayload = async (response) => {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  const readMessage = (payload) => {
    const candidates = [
      payload?.message,
      payload?.error?.message,
      payload?.error,
    ];

    return candidates.find(
      (value) => typeof value === "string" && value.trim()
    );
  };

  const createApiError = (response, payload, fallback) => {
    const error = new Error(readMessage(payload) || fallback);
    error.status = response.status;
    error.payload = payload;
    return error;
  };

  const parseApiResponse = async (response, fallback) => {
    const payload = await parsePayload(response);

    if (!response.ok || payload?.success === false) {
      throw createApiError(response, payload, fallback);
    }

    return payload;
  };

  const showAlert = ({ title, message, fields = [], showFields = false }) => {
    window.PeguuValidationAlert?.open({
      title,
      message,
      fields,
      showFields,
    });
  };

  const setFieldInvalid = (field, invalid) => {
    if (!field) return;

    if (invalid) {
      field.setAttribute("aria-invalid", "true");
    } else {
      field.removeAttribute("aria-invalid");
    }
  };

  const handlePasswordToggle = (button) => {
    const input = document.getElementById(button.dataset.target || "");

    if (!input) return;

    const shouldShow = input.type === "password";
    input.type = shouldShow ? "text" : "password";
    button.setAttribute("aria-pressed", String(shouldShow));
    button.setAttribute(
      "aria-label",
      shouldShow ? "Ocultar contraseña" : "Mostrar contraseña"
    );

    const eyeSlash = button.querySelector("[data-password-eye-slash]");
    if (eyeSlash) eyeSlash.style.display = shouldShow ? "none" : "";

    input.focus();
  };

  const setSubmitting = (button, text, submitting, loadingText) => {
    if (!button) return;

    if (!button.dataset.defaultText && text) {
      button.dataset.defaultText = text.textContent.trim();
    }

    button.disabled = submitting;
    button.setAttribute("aria-busy", submitting ? "true" : "false");

    if (text) {
      text.textContent = submitting
        ? loadingText
        : button.dataset.defaultText || text.textContent;
    }
  };

  const getApiBaseUrl = () =>
    window.PeguuApi?.endpoint?.("customers") || "/api/customers";

  const requestPasswordReset = async (email) => {
    const response = await fetch(`${getApiBaseUrl()}/request-password-reset`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    return parseApiResponse(
      response,
      "No fue posible enviar el enlace. Inténtalo de nuevo."
    );
  };

  const validateResetToken = async (token) => {
    const url = new URL(
      `${getApiBaseUrl()}/validate-reset-token`,
      window.location.origin
    );
    url.searchParams.set("token", token);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    return parseApiResponse(
      response,
      "El enlace para restablecer tu contraseña es inválido o expiró."
    );
  };

  const resetPassword = async (token, newPassword) => {
    const response = await fetch(`${getApiBaseUrl()}/reset-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, newPassword }),
    });

    return parseApiResponse(
      response,
      "No fue posible actualizar tu contraseña. Inténtalo de nuevo."
    );
  };

  const removeTokenFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const bindSection = (section) => {
    if (section.dataset.recoverBound === "true") return;
    section.dataset.recoverBound = "true";

    const requestView = section.querySelector("[data-recover-request-view]");
    const emailSentView = section.querySelector("[data-recover-email-sent-view]");
    const newPasswordView = section.querySelector("[data-recover-new-password-view]");
    const successView = section.querySelector("[data-recover-success-view]");
    const invalidTokenView = section.querySelector(
      "[data-recover-invalid-token-view]"
    );
    const requestForm = section.querySelector("[data-recover-request-form]");
    const newPasswordForm = section.querySelector("[data-recover-new-password-form]");
    const emailInput = section.querySelector("[data-recover-email-input]");
    const emailOutput = section.querySelector("[data-recover-email-output]");
    const requestButton = section.querySelector("[data-recover-request-submit]");
    const requestButtonText = section.querySelector("[data-recover-request-submit-text]");
    const newPasswordInput = section.querySelector("[data-recover-new-password]");
    const confirmPasswordInput = section.querySelector("[data-recover-confirm-password]");
    const saveButton = section.querySelector("[data-recover-save]");
    const saveButtonText = section.querySelector("[data-recover-save-text]");
    const showRequestButton = section.querySelector("[data-recover-show-request]");
    const passwordButtons = section.querySelectorAll(
      "[data-recover-password-toggle]"
    );
    const views = {
      request: requestView,
      email_sent: emailSentView,
      new_password: newPasswordView,
      success: successView,
      invalid_token: invalidTokenView,
    };
    let resetToken = trimValue(
      new URLSearchParams(window.location.search).get("token")
    );

    if (
      !requestView ||
      !emailSentView ||
      !newPasswordView ||
      !successView ||
      !invalidTokenView ||
      !requestForm ||
      !newPasswordForm
    ) {
      section.dataset.recoverReady = "true";
      return;
    }

    const setReady = () => {
      section.dataset.recoverReady = "true";
    };

    const showView = (view, focus = true) => {
      const activeView = views[view] || requestView;
      const activeTitle = activeView.querySelector("h1");

      Object.entries(views).forEach(([viewName, viewElement]) => {
        viewElement.hidden = viewName !== view;
      });

      section.dataset.recoverView = view;

      if (activeTitle) {
        section.setAttribute("aria-labelledby", activeTitle.id);
        if (focus) activeTitle.focus({ preventScroll: true });
      }
    };

    const setNewPasswordEnabled = (enabled) => {
      [newPasswordInput, confirmPasswordInput, saveButton].forEach((element) => {
        if (element) element.disabled = !enabled;
      });
    };

    const handleRequest = async (event) => {
      event.preventDefault();

      const email = trimValue(emailInput?.value);
      const invalidEmail = !EMAIL_PATTERN.test(email);

      setFieldInvalid(emailInput, invalidEmail);

      if (invalidEmail) {
        showAlert({
          title: "Revisa tu correo electrónico",
          message: "Ingresa un correo electrónico válido para continuar.",
        });
        emailInput?.focus();
        return;
      }

      setSubmitting(requestButton, requestButtonText, true, "Enviando…");

      try {
        await requestPasswordReset(email);
        if (emailOutput) emailOutput.textContent = email;
        showView("email_sent");
      } catch (error) {
        showAlert({
          title: "No pudimos enviar el enlace",
          message:
            error?.status === 429
              ? "Has realizado demasiados intentos. Espera un momento y vuelve a intentarlo."
              : error?.message || "No fue posible enviar el enlace.",
        });
      } finally {
        setSubmitting(requestButton, requestButtonText, false, "");
      }
    };

    const validatePasswords = () => {
      const newPassword = String(newPasswordInput?.value || "");
      const confirmation = String(confirmPasswordInput?.value || "");
      const failedRules = PASSWORD_RULES.filter(
        (rule) => !rule.test(newPassword)
      ).map((rule) => rule.label);
      const fields = [];

      if (!newPassword) fields.push("Nueva contraseña");
      if (!confirmation) fields.push("Confirmación de contraseña");
      if (newPassword && confirmation && newPassword !== confirmation) {
        fields.push("Las contraseñas deben coincidir");
      }

      setFieldInvalid(newPasswordInput, !newPassword || failedRules.length > 0);
      setFieldInvalid(
        confirmPasswordInput,
        !confirmation || newPassword !== confirmation
      );

      if (fields.length || failedRules.length) {
        showAlert({
          title: "Revisa tu nueva contraseña",
          message: "Corrige los siguientes puntos para continuar:",
          fields: [...failedRules, ...fields],
          showFields: true,
        });
        throw new Error("La nueva contraseña no cumple los requisitos.");
      }

      return newPassword;
    };

    const handleReset = async (event) => {
      event.preventDefault();

      if (!resetToken) {
        section.dataset.recoverState = "token_invalid";
        showView("invalid_token");
        return;
      }

      let newPassword;

      try {
        newPassword = validatePasswords();
      } catch (error) {
        return;
      }

      setSubmitting(saveButton, saveButtonText, true, "Guardando…");

      try {
        await resetPassword(resetToken, newPassword);
        resetToken = "";
        removeTokenFromUrl();
        newPasswordForm.reset();
        setFieldInvalid(newPasswordInput, false);
        setFieldInvalid(confirmPasswordInput, false);
        section.dataset.recoverState = "success";
        showView("success");
      } catch (error) {
        if (error?.status === 400) {
          resetToken = "";
          section.dataset.recoverState = "token_invalid";
          showView("invalid_token");
          return;
        }

        showAlert({
          title: "No pudimos actualizar tu contraseña",
          message: error?.message || "Inténtalo nuevamente.",
        });
      } finally {
        setSubmitting(saveButton, saveButtonText, false, "");
      }
    };

    const initializeToken = async () => {
      if (!resetToken) {
        setReady();
        return;
      }

      showView("new_password", false);
      section.setAttribute("aria-busy", "true");
      setNewPasswordEnabled(false);
      setSubmitting(saveButton, saveButtonText, true, "Validando…");

      try {
        const payload = await validateResetToken(resetToken);
        const data = payload?.data || payload || {};

        if (data.email && emailOutput) {
          emailOutput.textContent = data.email;
        }

        section.dataset.recoverState = "token_valid";
        setNewPasswordEnabled(true);
        setSubmitting(saveButton, saveButtonText, false, "");
        showView("new_password");
      } catch (error) {
        resetToken = "";

        if (error?.status === 400) {
          section.dataset.recoverState = "token_invalid";
          showView("invalid_token");
        } else {
          section.dataset.recoverState = "token_error";
          showView("request");
          showAlert({
            title: "No pudimos validar el enlace",
            message: error?.message || "Inténtalo nuevamente.",
          });
        }
      } finally {
        section.removeAttribute("aria-busy");
        if (section.dataset.recoverState !== "token_valid") {
          setSubmitting(saveButton, saveButtonText, false, "");
        }
        setReady();
      }
    };

    requestForm.addEventListener("submit", handleRequest);
    newPasswordForm.addEventListener("submit", handleReset);
    emailInput?.addEventListener("input", () => setFieldInvalid(emailInput, false));
    newPasswordInput?.addEventListener("input", () =>
      setFieldInvalid(newPasswordInput, false)
    );
    confirmPasswordInput?.addEventListener("input", () =>
      setFieldInvalid(confirmPasswordInput, false)
    );
    passwordButtons.forEach((button) => {
      button.addEventListener("click", () => handlePasswordToggle(button));
    });

    showRequestButton?.addEventListener("click", () => {
      removeTokenFromUrl();
      showView("request");
    });

    initializeToken();
  };

  const initialize = () => {
    document
      .querySelectorAll("[data-peguu-recover-password]")
      .forEach(bindSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
